import type {
  PdfEditorFieldBlockTemplate,
  PdfEditorFieldBlockField,
} from '@urbanx/agx.ui-components2';
import type { PreparedSignatory } from 'utils/vendorBuyerUtil';
import { nswContractExchangeDocuSignAnchors } from './contractExchangeDocuSignAnchors';

const REFERENCE_PAGE_WIDTH = 945;
const REFERENCE_PAGE_HEIGHT = 1336;
const INITIAL_FIELD_WIDTH = 88;
const INITIAL_FIELD_HEIGHT = 22;
const INITIAL_FIELD_GAP = 12;
const MAX_INITIAL_FIELDS_PER_PARTY = 6;

type InitialsParty = {
  label: 'Vendor' | 'Buyer';
  exportPrefix: 'Sellers' | 'Buyers';
};

const makeInitialField = (
  party: InitialsParty,
  signatoryIndex: number,
  fieldIndex: number
): PdfEditorFieldBlockField => {
  const displayIndex = signatoryIndex + 1;

  return {
    id: `${party.label.toLowerCase()}-${displayIndex}-initials`,
    label: `${party.label} ${displayIndex} Initials`,
    exportName: `${party.exportPrefix}${displayIndex}InitialHere`,
    docusignAnchorText:
      party.label === 'Vendor'
        ? nswContractExchangeDocuSignAnchors.sellerInitial(displayIndex)
        : nswContractExchangeDocuSignAnchors.buyerInitial(displayIndex),
    x: fieldIndex * (INITIAL_FIELD_WIDTH + INITIAL_FIELD_GAP),
    y: 0,
    width: INITIAL_FIELD_WIDTH,
    height: INITIAL_FIELD_HEIGHT,
  };
};

export const buildContractExchangeInitialsTemplate = ({
  vendorSignatories = [],
  buyerSignatories = [],
}: {
  vendorSignatories?: PreparedSignatory[];
  buyerSignatories?: PreparedSignatory[];
}): PdfEditorFieldBlockTemplate | undefined => {
  const supportedVendorSignatories = vendorSignatories.slice(0, MAX_INITIAL_FIELDS_PER_PARTY);
  const supportedBuyerSignatories = buyerSignatories.slice(0, MAX_INITIAL_FIELDS_PER_PARTY);

  const fields = [
    ...supportedVendorSignatories.map((_, index) =>
      makeInitialField(
        {
          label: 'Vendor',
          exportPrefix: 'Sellers',
        },
        index,
        index
      )
    ),
    ...supportedBuyerSignatories.map((_, index) =>
      makeInitialField(
        {
          label: 'Buyer',
          exportPrefix: 'Buyers',
        },
        index,
        supportedVendorSignatories.length + index
      )
    ),
  ];

  if (fields.length === 0) {
    return undefined;
  }

  return {
    id: 'contract-exchange-initials',
    label: 'Initials',
    buttonLabel: 'Add Initials',
    initialPlacement: {
      pageIndex: 0,
      x: 70,
      y: 70,
      referencePageWidth: REFERENCE_PAGE_WIDTH,
      referencePageHeight: REFERENCE_PAGE_HEIGHT,
      placeInViewport: true,
    },
    fields,
  };
};
